import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  styled,
  Autocomplete,
  Avatar,
  useTheme,
  CircularProgress,
  SelectChangeEvent,
  Grid2,
  ListItemIcon,
  Tab,
  FormControl,
  InputLabel,
  Drawer,
  Button,
  Dialog,
  Select,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormGroup,
  Chip,
} from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import AnnotationEditor from "./AnnotationEditor";
import AttachmentViewModal from "./AttachmentViewModal";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import moment from "moment";

import {
  useCreateTagMutation,
  useGetConfigDetailsQuery,
  useGetListOfBugsQuery,
  useGetListOfProjectsQuery,
  useGetListOfStatusQuery,
  useGetListOfTagsQuery,
  useGetProjectByIdQuery,
  useGetProjectStatusLabelsMutation,
  useGetSprintListQuery,
} from "@/features/bughurd/api";
import { useNavigate, useParams } from "react-router-dom";
import storageService from "@/Components/helpers/storageService";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import { hexToRgba } from "@/Components/helpers/util";
import { StyledChip, StyledSelect, StyledTextField } from "./styled";
import RichTextEditor from "@/Components/Common/RichTextEditor";
import {
  getActiveSprints,
  getDraftSprints,
  getOperatingSystemInfo,
} from "@/pages/Board/utils";
import AttachmentListView from "./AttachmentListView";
import AttachmentGridView from "./AttachmentGridView";
import RecurringTaskPopover, {
  RecurringConfig,
} from "@/pages/Board/components/RecurringTaskPopover";
import Relationship from "../components/Relationship";
import LogsTab from "../BugHurd/logs";
import VideoPreviewPanel from "./VideoPreviewPanel";
import { useAppSelector } from "@/app/hooks";
import { selectBoardState } from "@/features/bughurd/boardSelector";

const DropZone = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[400]}`,
  borderRadius: "8px",
  padding: theme.spacing(0.5),
  textAlign: "center",
  backgroundColor: theme.palette.grey[50],
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "10",
  },
  "&.drag-over": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + "20",
  },
}));

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  coordinates?: { left: number; top: number } | null;
  selectedElement?: HTMLElement | null;
  pageUrl: string;
  screenshotData?: string | null;
  users: any[];
  onSubmit: (
    formData: TaskData,
    attachments: AttachmentType[]
  ) => Promise<void>;
  isSubmitting: boolean;
  selector?: string;
  fullPageScreenshotData?: string | null;
  imageUpload: boolean;
  blobDataForFullPage?: Blob | null;
  recordedURL?: string;
  recordBugLoading?: boolean;
  videoRecording?: Blob | null | any;
  status?: string | any;
  refreshColumnData?: any;
  consoleLogs?: any[];
  networkLogs?: any[];
  permissions?: any;
}

export interface AttachmentType {
  fileName: string;
  fileType: string;
  url: string;
  serverUrl?: string;
  isUploading?: boolean;
  uploadFailed?: boolean;
  isAnnotated?: boolean;
}

interface TaskData {
  summary: string;
  description: string;
  platform: string;
  severity: string;
  status: string;
  tags: string[];
  assignees: string[];
  keepSettings: boolean;
  screenshot: string | null;
  fullPageScreenshot: string | null;
  project: string;
  projectKey: string;
  bugStatus?: string;
  jiraPriority?: string;
  jiraStatus?: string;
  pageUrl?: string;
  isSettings?: boolean;
  selector?: string;
  screenShotKey?: string;
  fullPageScreenshotKey?: string;
  recordingLink?: string;
  type?: string;
  videoRecording?: Blob | null | any;
  jiraProjectKey?: string;
  jiraProjectName?: string;
  jiraProjectId?: string;
  startDate?: string;
  dueDate?: string;
  estimation?: string;
  estimationDays?: string;
  estimationHours?: string;
  estimationMinutes?: string;
  isRecurring?: boolean;
  recurringData?: any;
  boardType?: string;
  currentSprint?: string | null;
  isBackLog?: boolean;
  requiredSkillSet?: string;
}

export const defaultTaskData: TaskData = {
  summary: "",
  description: "",
  platform: "",
  severity: "",
  status: "",
  tags: [],
  assignees: [],
  keepSettings: false,
  screenshot: null,
  fullPageScreenshot: null,
  project: "",
  projectKey: "",
  screenShotKey: "",
  fullPageScreenshotKey: "",
  recordingLink: "",
  type: "",
  videoRecording: null,
  jiraProjectKey: "",
  jiraProjectName: "",
  jiraProjectId: "",
  startDate: null,
  dueDate: null,
  estimation: "",
  estimationDays: "",
  estimationHours: "",
  estimationMinutes: "",
  isRecurring: false,
  recurringData: null,
  boardType: "",
  currentSprint: null,
  isBackLog: false,
  requiredSkillSet: "",
};

const TaskCreateDrawer: React.FC<TaskCreateModalProps> = ({
  open,
  onClose,
  pageUrl,
  users,
  screenshotData,
  onSubmit,
  isSubmitting,
  selector,
  fullPageScreenshotData,
  imageUpload,
  blobDataForFullPage,
  recordedURL,
  recordBugLoading,
  videoRecording,
  status,
  refreshColumnData,
  consoleLogs,
  networkLogs,
  permissions,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [taskData, setTaskData] = useState<TaskData>(defaultTaskData);
  const [tagInput, setTagInput] = useState("");
  const [isAnnotationEditorOpen, setIsAnnotationEditorOpen] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const [_statusList, setStatusList] = useState([]);
  const [isJiraConfigured, setIsJiraConfigured] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<AttachmentType | null>(null);
  const [isAttachmentViewOpen, setIsAttachmentViewOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<Set<string>>(new Set());
  const boardState = useAppSelector(selectBoardState);
  // Store original filenames for display purposes (not sent to backend)
  const [originalFilenames, setOriginalFilenames] = useState<
    Map<string, string>
  >(new Map());
  const { id } = useParams();
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("1");
  const [attachmentView, setAttachmentView] = useState("grid"); // "list" or "grid"
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [combinedAttachments, setCombinedAttachments] = useState([]);
  const [combiningAttachments, setCombiningAttachments] = useState(false);

  // Validation error states
  const [statusError, setStatusError] = useState("");
  const [severityError, setSeverityError] = useState("");
  const [issueTypeError, setIssueTypeError] = useState("");
  const [projectError, setProjectError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [sprintError, setSprintError] = useState("");

  const [dependenciesAnchorEl, setDependenciesAnchorEl] =
    useState<HTMLElement | null>(null);
  const [dependencies, setDependencies] = useState<any[]>([]);

  // Updated recurring task states
  const [recurringAnchorEl, setRecurringAnchorEl] =
    useState<HTMLElement | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig>({
    isRecurring: false,
    frequency: "Daily",
    trigger: "When done",
    skipWeekends: false,
    createNewTask: true,
    updateStatusTo: "",
    repeatTimes: 5,
    customInterval: 1,
    customUnit: "week",
    selectedDays: [],
    selectedMonthlyDays: [],
    monthlyOption: "same_day",
    startDate: new Date(),
    endOnDate: new Date(),
    isEndOn: false,
    selectedTime: "09:00",
  });
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);

  // Annotation states
  const [isAnnotatingAttachment, setIsAnnotatingAttachment] = useState(false);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(null);

  // Video preview state
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  // System info state for Info tab
  const [systemInfo, setSystemInfo] = useState<{
    url?: string;
    timestamp?: string | Date;
    os?: string;
    browser?: string;
    windowSize?: string;
    screenshotDimensions?: string;
  } | null>(null);

  // Fetch system info when drawer opens
  useEffect(() => {
    const fetchSystemInfo = async () => {
      if (open) {
        const osDetail = await getOperatingSystemInfo();

        // Calculate screenshot dimensions if available
        let screenshotDims = "";
        if (fullPageScreenshotData) {
          const img = new Image();
          img.src = fullPageScreenshotData;
          await new Promise((resolve) => {
            img.onload = () => {
              screenshotDims = `${img.naturalWidth}x${img.naturalHeight}`;
              resolve(null);
            };
            img.onerror = () => resolve(null);
          });
        }

        setSystemInfo({
          url: pageUrl || window.location.href,
          timestamp: new Date(),
          os: osDetail?.operatingSystem || "",
          browser: osDetail?.browser || "",
          windowSize: osDetail?.screen || `${window.innerWidth}x${window.innerHeight}`,
          screenshotDimensions: screenshotDims,
        });
      }
    };
    fetchSystemInfo();
  }, [open, pageUrl, fullPageScreenshotData]);

  const { data: tagList, refetch: refetchTags } = useGetListOfTagsQuery(
    taskData.project,
    {
      skip: !taskData.project,
    }
  );
  const availableTags = tagList?.data?.decryptedData?.items || [];

  const [createTag] = useCreateTagMutation();

  const { data: statusLists } = useGetListOfStatusQuery(
    { projectId: taskData.project },
    {
      skip: !taskData.project,
    }
  );

  const { data: sprintListData, isLoading: isSprintLoading } =
    useGetSprintListQuery(
      {
        projectId: taskData.project || "",
        page: 1,
        limit: 100,
        statuses: ["DRAFT", "ACTIVE"],
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !taskData.project,
      }
    );
  const sprints = sprintListData?.data?.decryptedData?.items || [];
  const draftSprints = getDraftSprints(sprints);
  const activeSprints = getActiveSprints(sprints);

  const availableStatus = statusLists?.data?.decryptedData?.items || [];

  const taskDataKeepdecryptedData = JSON.parse(
    localStorage.getItem("taskDataKeep")
  );
  const { data: epicData } = useGetListOfBugsQuery(
    {
      id: taskData.project,
      forBoard: true,
      viewType: "TIMELINE",
      showEpic: true,
      page: 1,
      limit: 100,
    },
    {
      skip: !taskData.project,
      refetchOnMountOrArgChange: true,
    }
  );
  const epics = useMemo(() => {
    if (!epicData?.data?.decryptedData?.items) return [];
    return epicData?.data?.decryptedData?.items.map((item) => ({
      id: item?._id,
      name: item.summary,
    }));
  }, [epicData]);
  const shouldShowSprintDropdown = useMemo(() => {
    return taskData.boardType === "SCRUM";
  }, [taskData.boardType]);
  // Reset form when project ID changes
  useEffect(() => {
    if (id) {
      // Reset taskData to default state but keep some settings if keepSettings was true
      const shouldKeepSettings =
        taskData.keepSettings && taskDataKeepdecryptedData;

      if (shouldKeepSettings) {
        // If keepSettings is true, preserve some fields but reset project-specific ones
        setTaskData((prev) => ({
          ...defaultTaskData,
          // Keep non-project-specific settings
          platform: taskDataKeepdecryptedData.platform || "JIRA",
          severity: taskDataKeepdecryptedData.severity || "",
          type: taskDataKeepdecryptedData.type || "",
          keepSettings: taskDataKeepdecryptedData.keepSettings || false,
          // Set the new project
          project: id,
          // Reset project-specific fields
          status: "",
          assignees: [],
          tags: [],
          jiraProjectKey: "",
          jiraProjectName: "",
          jiraProjectId: "",
          boardType: "", // Reset board type
          currentSprint: null, // Reset sprint selection
          isBackLog: false, // Reset backlog flag
        }));
      } else {
        // Complete reset with new project ID
        setTaskData((prev) => ({
          ...defaultTaskData,
          project: id,
          assignees: [],
        }));
      }

      // Clear form data for Jira fields
      setFormData({});

      // Clear validation errors
      setStatusError("");
      setSeverityError("");
      setIssueTypeError("");
      setProjectError("");

      // Reset recurring states
      setIsRecurring(false);
      setRecurringConfig({
        isRecurring: false,
        frequency: "Daily",
        trigger: "When done",
        skipWeekends: false,
        createNewTask: true,
        updateStatusTo: "",
        repeatTimes: 5,
        customInterval: 1,
        customUnit: "week",
        selectedDays: [],
        selectedMonthlyDays: [],
        monthlyOption: "same_day",
        startDate: new Date(),
        endOnDate: new Date(),
        isEndOn: false,
        selectedTime: "09:00",
      });

      // Reset dependencies
      setDependencies([]);
      setDependenciesAnchorEl(null);
    }
  }, [id]); // Only depend on id changes
  const [getProjectStatusLabels, { isLoading: isLoadingStatusLabels }] =
    useGetProjectStatusLabelsMutation();
  const { data: configDataList } = useGetConfigDetailsQuery({});
  const configData = configDataList?.data?.decryptedData;
  const { projects: projectList = [] } = useAppSelector((state) => state.board) || {};

  const { data: projectDetails, isLoading: isProjectLoading } =
    useGetProjectByIdQuery(taskData.project, {
      skip: !taskData.project,
      refetchOnMountOrArgChange: true,
    });
  const jiraProjectList = projectDetails?.data?.decryptedData?.jiraConfig;
  const projectUserIds = (
    projectDetails?.data?.decryptedData?.users || []
  ).map((user) => {
    if (typeof user === "object" && user?._id) {
      return user._id;
    }

    return user;
  });
  useEffect(() => {
    if (
      projectDetails?.data?.decryptedData?.boardType &&
      taskData.project &&
      !taskData.boardType
    ) {
      setTaskData((prev) => ({
        ...prev,
        boardType: projectDetails?.data?.decryptedData?.boardType,
      }));
    }
  }, [projectDetails, taskData.project, taskData.boardType]);
  const availableUsers = useMemo(() => {
    if (!taskData?.project) {
      return [];
    }
    const projectOwnerId = projectDetails?.data?.decryptedData?.owner;
    return (
      users?.filter((user) =>
        [projectUserIds, projectOwnerId].flat().includes(user._id)
      ) || []
    );
  }, [taskData?.project, projectUserIds, users]);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleChange = (fieldKey: string, value: string, type?: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: type === "number" ? Number(value) : value,
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowForMenu(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowForMenu(null);
  };
  const getDefaultFields = (jiraProjectKey) => {
    const selectedProject =
      projectDetails?.data?.decryptedData.jiraConfig.find(
        (project) => project.key === jiraProjectKey
      );

    return selectedProject ? selectedProject.defaultFields : null;
  };

  const defaultFields = getDefaultFields(taskData?.jiraProjectKey);

  const severityOptions = [
    { value: "Highest", label: t("Highest"), icon: <HighestIcon /> },
    { value: "High", label: t("High"), icon: <HighIcon /> },
    { value: "Medium", label: t("Medium"), icon: <MediumIcon /> },
    { value: "Low", label: t("Low"), icon: <LowIcon /> },
  ];
  const issueType = [
    { value: "STORY", label: t("STORY") },
    { value: "BUG", label: t("BUG") },
    { value: "EPIC", label: t("EPIC") },
  ];
  const issueTypeItems = projectDetails?.data?.decryptedData?.itemTypes
    ?.length
    ? projectDetails?.data?.decryptedData?.itemTypes
    : issueType;
  // Generate minutes options (0, 15, 30, 45)
  const minuteOptions = [
    { value: "0", label: "0" },
    { value: "15", label: "15" },
    { value: "30", label: "30" },
    { value: "45", label: "45" },
  ];
  const skillSetOptions = [
    "Frontend Development",
    "Backend Development",
    "Full Stack Development",
    "DevOps",
    "QA/Testing",
    "UI/UX Design",
    "Mobile Development",
    "Data Science",
    "Machine Learning",
    "Security",
  ];

  // Function to generate unique filename while preserving original for display
  const generateUniqueFilename = (originalFileName: string) => {
    const timestamp = new Date().getTime();
    const randomId = Math.random().toString(36).substring(2, 15);
    const lastDotIndex = originalFileName.lastIndexOf(".");

    if (lastDotIndex === -1) {
      // No extension
      return `${originalFileName}_${timestamp}_${randomId}`;
    }

    const nameWithoutExtension = originalFileName.substring(0, lastDotIndex);
    const extension = originalFileName.substring(lastDotIndex);

    return `${nameWithoutExtension}_${timestamp}_${randomId}${extension}`;
  };

  // Function to update estimation based on days, hours and minutes
  const updateEstimation = (days: string, hours: string, minutes: string) => {
    const daysNum = parseInt(days || "0");
    const hoursNum = parseInt(hours || "0");
    const minutesNum = parseInt(minutes || "0");

    const parts = [];
    if (daysNum > 0) {
      parts.push(`${daysNum}d`);
    }
    if (hoursNum > 0) {
      parts.push(`${hoursNum}h`);
    }
    if (minutesNum > 0) {
      parts.push(`${minutesNum}m`);
    }

    return parts.join(" ");
  };

  // Function to convert frontend recurring config to backend format
  const convertToBackendRecurringData = (config: RecurringConfig) => {
    const baseData = {
      completionStatus:
        config.trigger === "When done" ? "WHEN_DONE" : "ON_SCHEDULE",
      isCreate: config.createNewTask || false,
      isUpdate: !!config.updateStatusTo,
      updationStatus: config.updateStatusTo || null,
      isRepeat: config.repeatTimes > 0,
      repeatMargin: config.repeatTimes || 0,
      isEndOn: config.isEndOn || false,
      endOn:
        config.isEndOn && config.endOnDate
          ? moment(config.endOnDate).format("YYYY-MM-DD HH:mm")
          : null,
    };

    // Handle different frequencies
    if (config.frequency === "Daily") {
      return {
        executionInfo: {
          executionFrequencyUnit: "DAYS",
          executionFrequencyInterval: 1,
          executionTime: config.selectedTime || "09:00",
          skipWeekends: config.skipWeekends || false,
        },
        ...baseData,
      };
    } else if (config.frequency === "Weekly") {
      const weekdayMap = {
        Su: "SUNDAY",
        Mo: "MONDAY",
        Tu: "TUESDAY",
        We: "WEDNESDAY",
        Th: "THURSDAY",
        Fr: "FRIDAY",
        Sa: "SATURDAY",
      };

      const selectedDays = (config.selectedDays || [])
        .map((day) => weekdayMap[day])
        .filter(Boolean);

      return {
        executionInfo: {
          executionFrequencyUnit: "WEEKS",
          executionDayInWeek:
            selectedDays.length === 1 ? selectedDays[0] : selectedDays,
          executionTime: config.selectedTime || "09:00",
          executionFrequencyInterval: 1,
        },
        ...baseData,
      };
    } else if (config.frequency === "Monthly") {
      const monthOptionMap = {
        same_day: "SAME_DAY",
        third_tuesday: "THIRD_TUESDAY",
        first_day: "FIRST_DAY",
        last_day: "LAST_DAY",
      };

      const executionInfo = {
        executionFrequencyUnit: "MONTHS",
        monthOption: monthOptionMap[config.monthlyOption] || "SAME_DAY",
        executionTime: config.selectedTime || "09:00",
        executionFrequencyInterval: 1,
      };

      if (config.monthlyOption === "same_day" && config.startDate) {
        // @ts-ignore
        executionInfo.customStartDate = moment(config.startDate).format(
          "YYYY-MM-DD HH:mm"
        );
      }

      return {
        executionInfo,
        ...baseData,
      };
    }

    // Default fallback
    return {
      executionInfo: {
        executionFrequencyUnit: "DAYS",
        executionFrequencyInterval: 1,
        executionTime: config.selectedTime || "09:00",
        skipWeekends: config.skipWeekends || false,
      },
      ...baseData,
    };
  };

  // Updated recurring task handlers
  const handleRecurringToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setIsRecurring(checked);

    if (checked) {
      // Set anchor element to the form control label
      setRecurringAnchorEl(
        event.currentTarget.closest(".MuiFormControlLabel-root") as HTMLElement
      );

      // Set default values for recurring config when enabling
      const defaultConfig = {
        ...recurringConfig,
        isRecurring: true,
        updateStatusTo: availableStatus[0]?._id || "",
      };
      setRecurringConfig(defaultConfig);
      setIsRecurringModalOpen(true);
    } else {
      // Disable recurring
      setRecurringConfig((prev) => ({
        ...prev,
        isRecurring: false,
      }));
      setRecurringAnchorEl(null);
    }
  };

  const handleRecurringConfigClick = (event: React.MouseEvent<HTMLElement>) => {
    setRecurringAnchorEl(
      event.currentTarget.closest(".MuiFormControlLabel-root") as HTMLElement
    );
    setIsRecurringModalOpen(true);
  };

  const handleRecurringConfigSave = (config: RecurringConfig) => {
    // Ensure the config has isRecurring set to true
    const configWithRecurring = {
      ...config,
      isRecurring: true,
    };

    setRecurringConfig(configWithRecurring);
    setIsRecurring(true);
    setIsRecurringModalOpen(false);
    setRecurringAnchorEl(null);
  };

  const handleRecurringModalClose = () => {
    setIsRecurringModalOpen(false);
    setRecurringAnchorEl(null);
    // If user closes modal without saving and recurring was just turned on, turn it off
    if (!recurringConfig.isRecurring) {
      setIsRecurring(false);
    }
  };

  const getRecurringDisplayText = () => {
    if (!isRecurring || !recurringConfig.isRecurring) return "";

    const frequency = recurringConfig.frequency;
    const trigger = recurringConfig.trigger;

    return `${frequency} - ${trigger}`;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };
  useEffect(() => {
    if (boardState?.dueDateDateForCalendarView) {
      const dueDate = new Date(boardState.dueDateDateForCalendarView);
      const formattedDate = dueDate.toISOString().split("T")[0];
      setTaskData((prev) => ({
        ...prev,
        dueDate: formattedDate,
      }));
    }
    if (boardState?.selectedColumnStatus) {
      setTaskData((prev) => ({
        ...prev,
        status: boardState?.selectedColumnStatus,
      }));
    }
  }, [
    boardState?.dueDateDateForCalendarView,
    boardState?.selectedColumnStatus,
  ]);
  const base64ToBlob = (base64String) => {
    const byteCharacters = atob(base64String.split(",")[1]);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    return new Blob([new Uint8Array(byteArrays)], { type: "image/png" });
  };

  const uploadScreenshot = async (
    base64Screenshot?: string,
    blobData?: Blob,
    originalName?: string
  ) => {
    if (!base64Screenshot && !blobData) return null;

    const finalBlob = blobData || base64ToBlob(base64Screenshot!);

    // Generate unique filename for storage
    const uniqueFilename = originalName
      ? generateUniqueFilename(originalName)
      : `screenshot_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2)}.png`;

    const formData = new FormData();
    formData.append("file", finalBlob, uniqueFilename);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      return response?.data?.data?.localData?.Key || null;
    } catch (error) {
      console.error("Screenshot upload failed:", error);
      return null;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (name === "summary" && value.length > 250) {
      toast.warning(t("Summary should be less than 250 characters"));
      return;
    }

    if (name === "description" && value.length > 2000) {
      toast.warning(t("Description should be less than 2000 characters"));
      return;
    }

    // For estimation days and hours inputs, only allow numbers
    if (
      (name === "estimationDays" || name === "estimationHours") &&
      value !== ""
    ) {
      const numValue = parseInt(value);
      if (isNaN(numValue) || numValue < 0) {
        return; // Don't update if invalid number
      }
    }

    setTaskData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleRichTextEditor = (e) => {
    setTaskData((prev) => ({
      ...prev,
      description: e,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target;

    if (name === "project") {
      // Find the selected project to get its boardType
      const selectedProject = projectList?.find(
        (project) => project._id === value
      );
      const boardType = selectedProject?.boardType || "";
      let savedDataInStorage = JSON.parse(localStorage.getItem("taskDataKeep"));
      if (savedDataInStorage) {
        savedDataInStorage.assignees = [];
        localStorage.setItem(
          "taskDataKeep",
          JSON.stringify(savedDataInStorage)
        );
      }
      setTaskData((prev) => ({
        ...prev,
        project: value as string, // Type assertion to string
        boardType: boardType,
        status: "", // Clear status when project changes
        currentSprint: null, // Reset sprint selection when project changes
        isBackLog: false, // Reset backlog flag when project changes
        assignees: [],
      }));
    } else {
      if (name === "type" && value === "EPIC") {
        setSelectedEpic(null);
      }
      setTaskData((prev) => ({
        ...prev,
        [name]: value as string, // Type assertion to string
        // Clear status when project changes
        ...(name === "project" && { status: "" }),
      }));
    }

    // Clear validation errors when values are selected
    if (name === "status" && value) {
      setStatusError("");
    }
    if (name === "severity" && value) {
      setSeverityError("");
    }
    if (name === "type" && value) {
      setIssueTypeError("");
    }
    if (name === "project" && value) {
      setProjectError("");
    }
  };

  const resetForm = () => {
    setTaskData((p) => ({
      ...defaultTaskData,
      type: "",
      currentSprint: null,
      isBackLog: false,
      requiredSkillSet: "",
    }));
    // Clear all validation errors on reset
    setStatusError("");
    setSeverityError("");
    setIssueTypeError("");
    setProjectError("");
    setTagInput("");
    setAttachments([]);
    setPendingUploads(new Set());
    setOriginalFilenames(new Map());
    setCombinedAttachments([]);
    // Reset recurring states
    setIsRecurring(false);
    setRecurringConfig({
      isRecurring: false,
      frequency: "Daily",
      trigger: "When done",
      skipWeekends: false,
      createNewTask: true,
      updateStatusTo: "",
      repeatTimes: 5,
      customInterval: 1,
      customUnit: "week",
      selectedDays: [],
      selectedMonthlyDays: [],
      monthlyOption: "same_day",
      startDate: new Date(),
      endOnDate: new Date(),
      isEndOn: false,
      selectedTime: "09:00",
    });
    setDependencies([]);
    setDependenciesAnchorEl(null);
  };

  const handleViewAttachment = (attachment: AttachmentType) => {
    // If it's a video, show the video preview panel
    if (attachment.type === "video" && attachment.url) {
      setPreviewVideoUrl(attachment.url);
      setIsVideoPreviewOpen(true);
    } else {
      // Otherwise show the standard attachment view modal
      setSelectedAttachment(attachment);
      setIsAttachmentViewOpen(true);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    const attachment = attachments[index];
    // Remove from pending uploads if it exists
    if (attachment.fileName) {
      setPendingUploads((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.fileName);
        return newSet;
      });
      // Remove from original filenames map
      setOriginalFilenames((prev) => {
        const newMap = new Map(prev);
        newMap.delete(attachment.fileName);
        return newMap;
      });
    }

    setAttachments((prev) => prev.filter((_, i) => i !== index));
    toast.success(t("Attachment removed successfully"));
  };

  // Function to handle removing screenshots
  const handleRemoveScreenshot = () => {
    setTaskData((prev) => ({
      ...prev,
      screenshot: null,
      fullPageScreenshot: null,
      screenShotKey: "",
      fullPageScreenshotKey: "",
    }));
    toast.success(t("Screenshot removed successfully"));
  };

  // Function to handle removing video recording
  const handleRemoveVideoRecording = () => {
    setTaskData((prev) => ({
      ...prev,
      videoRecording: null,
      recordingLink: "",
    }));
    toast.success(t("Video recording removed successfully"));
  };

  // Handle paste from system clipboard (for files)
  const handleSystemPaste = async (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      event.preventDefault();
      await processFiles(files);
      toast.success(t(`${files.length} file(s) pasted from clipboard`));
    }
  };

  const processFiles = async (files: File[]) => {
    setIsUploading(true);

    for (const file of files) {
      const originalFileName = file.name;
      const uniqueFileName = generateUniqueFilename(originalFileName);

      // Store original filename for display purposes
      setOriginalFilenames((prev) =>
        new Map(prev).set(uniqueFileName, originalFileName)
      );

      // Add to pending uploads with unique filename for tracking
      setPendingUploads((prev) => new Set(prev).add(uniqueFileName));

      if (!file.type.startsWith("image/")) {
        const newAttachment: AttachmentType = {
          fileType: file.type,
          fileName: uniqueFileName, // Use unique filename for backend
          url: null,
          isUploading: true,
        };
        setAttachments((prev) => [...prev, newAttachment]);

        // For non-image files, we still need to upload them
        try {
          const formData = new FormData();
          formData.append("file", file, uniqueFileName);

          const response = await axios.post(
            `${import.meta.env.VITE_COMPANY_API_URL}/file/upload`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              withCredentials: true,
            }
          );

          const uploadUrl = response?.data?.data?.localData?.Key || null;

          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === null
                ? {
                    ...att,
                    serverUrl: uploadUrl,
                    isUploading: false,
                  }
                : att
            )
          );
        } catch (error) {
          console.error("Error uploading file:", error);
          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === null
                ? {
                    ...att,
                    isUploading: false,
                    uploadFailed: true,
                  }
                : att
            )
          );
        } finally {
          setPendingUploads((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueFileName);
            return newSet;
          });
        }
        continue;
      }

      const reader = new FileReader();

      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;

        const newAttachment: AttachmentType = {
          fileType: file.type,
          fileName: uniqueFileName, // Use unique filename for backend
          url: base64Data,
          isUploading: true,
        };

        setAttachments((prev) => [...prev, newAttachment]);

        try {
          const uploadUrl = await uploadScreenshot(
            base64Data,
            undefined,
            originalFileName
          );

          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === base64Data
                ? {
                    ...att,
                    serverUrl: uploadUrl,
                    isUploading: false,
                  }
                : att
            )
          );
        } catch (error) {
          console.error("Error uploading image:", error);
          setAttachments((prev) =>
            prev.map((att) =>
              att.fileName === uniqueFileName && att.url === base64Data
                ? {
                    ...att,
                    isUploading: false,
                    uploadFailed: true,
                  }
                : att
            )
          );
        } finally {
          setPendingUploads((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueFileName);
            return newSet;
          });
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
        toast.error(t("Error reading file"));
        setPendingUploads((prev) => {
          const newSet = new Set(prev);
          newSet.delete(uniqueFileName);
          return newSet;
        });
      };

      reader.readAsDataURL(file);
    }

    setIsUploading(false);
  };

  // Validation function
  const validateForm = () => {
    let hasErrors = false;

    if (!taskData?.summary.trim()) {
      toast.error(t("Please add a summary"));
      hasErrors = true;
    }

    if (!taskData?.project) {
      setProjectError(t("Project is required"));
      hasErrors = true;
    }

    if (!taskData?.severity) {
      setSeverityError(t("Severity is required"));
      hasErrors = true;
    }

    if (!taskData?.status) {
      setStatusError(t("Status is required"));
      hasErrors = true;
    }

    if (!taskData?.type) {
      setIssueTypeError(t("Issue Type is required"));
      hasErrors = true;
    }

    return !hasErrors;
  };

  const formatDependenciesForAPI = (dependencies: any[]) => {
    const linkBugs = dependencies.map((task) => ({
      type:
        task.status === "Waiting On"
          ? "Waiting"
          : task.status === "Blocking"
          ? "Blocked"
          : "Linked",
      bug: task._id,
    }));

    return linkBugs;
  };

  // Check if there are pending uploads
  const hasPendingUploads = () => {
    return (
      pendingUploads.size > 0 || attachments.some((att) => att.isUploading)
    );
  };

  const handleButtonClick = () => {
    if (hasPendingUploads()) {
      toast.warning(
        t("Please wait for all uploads to complete before creating the task")
      );
      return;
    }

    if (validateForm()) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    // Final check to ensure no uploads are pending
    if (hasPendingUploads()) {
      toast.warning(
        t("Please wait for all uploads to complete before creating the task")
      );
      return;
    }

    // Check if any attachments failed to upload
    const failedUploads = attachments.filter((att) => att.uploadFailed);
    if (failedUploads.length > 0) {
      toast.error(
        t("Some attachments failed to upload. Please remove them or try again.")
      );
      return;
    }

    const osDetail = await getOperatingSystemInfo();
    setIsLoading(true);
    try {
      const processedAttachments = attachments.map((att) => ({
        ...att,
        url: att.serverUrl || att.url,
        fileName: att.fileName, // This is already the unique filename
        fileType: att.fileType,
      }));

      // Combine days, hours and minutes into estimation string
      const estimation = updateEstimation(
        taskData.estimationDays || "",
        taskData.estimationHours || "",
        taskData.estimationMinutes || ""
      );

      // Convert recurring config to backend format
      const backendRecurringData =
        isRecurring && recurringConfig.isRecurring
          ? convertToBackendRecurringData(recurringConfig)
          : null;

      const sprintPayload =
        taskData.boardType === "SCRUM"
          ? {
              ...(taskData.currentSprint
                ? { currentSprint: taskData.currentSprint, isBackLog: false }
                : { isBackLog: true }),
            }
          : {};
      const epicPayload = selectedEpic ? { epic: selectedEpic } : {};
      const enhancedTaskData = {
        ...taskData,
        projectKey: taskData?.projectKey,
        bugStatus: taskData?.status,
        severity: taskData?.severity,
        jiraPriority: "1",
        jiraStatus: "11",
        isSettings: taskData?.keepSettings,
        pageUrl: pageUrl,
        recordingLink: taskData?.recordingLink,
        videoRecording: taskData?.videoRecording,
        jiraDatas: formData,
        jiraProjectKey: taskData?.jiraProjectKey,
        project: taskData?.project,
        jiraProjectName: taskData.jiraProjectName,
        jiraProjectId: taskData.jiraProjectId,
        startDate: taskData?.startDate,
        dueDate: taskData?.dueDate,
        estimation: estimation,
        deviceDetails: JSON.stringify(osDetail),
        isRecurring: isRecurring,
        recurringData: backendRecurringData,
        linkBugs: formatDependenciesForAPI(dependencies),
        requiredSkillSet: taskData.requiredSkillSet || "",
        ...sprintPayload,
        ...epicPayload,
      };

      await onSubmit(enhancedTaskData, processedAttachments);
      setTaskData((prev) => ({
        ...prev,
        currentSprint: null,
        isBackLog: false,
      }));
      setSelectedEpic(null);
      resetForm();
      refreshColumnData();
      onClose();
      setFormData({});
    } catch (error) {
      console.error("Error creating bug:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      await processFiles(Array.from(files));
      // Clear the input value so the same file can be selected again
      event.target.value = "";
    }
  };

  const handleAssigneeChange = (_, newValues) => {
    // Ensure newValues is always an array
    const valuesArray = Array.isArray(newValues) ? newValues : [];

    setTaskData((prev) => ({
      ...prev,
      assignees: valuesArray.map((user) => user._id),
    }));
  };

  const handleSaveAnnotation = async (annotatedImageData: string) => {
    try {
      // Generate a unique filename to prevent overwriting
      const uniqueFilename = generateUniqueFilename(
        "annotated_fullpage_screenshot.png"
      );

      // Only update the fullPageScreenshot, not the element screenshot
      setTaskData((prev) => ({
        ...prev,
        fullPageScreenshot: annotatedImageData, // Only update fullpage screenshot
        // Do NOT update screenshot here - keep element screenshot separate
      }));

      // Upload annotated image to server with unique filename
      const url = await uploadScreenshot(
        annotatedImageData,
        undefined,
        "annotated_fullpage_screenshot.png"
      );

      // Only update fullPageScreenshotKey, not screenShotKey
      setTaskData((prev) => ({
        ...prev,
        fullPageScreenshotKey: url,
        // Do NOT update screenShotKey here - keep element screenshot key separate
      }));
    } catch (error) {
      console.error("Error uploading annotated image:", error);
    } finally {
      setIsAnnotationEditorOpen(false);
    }
  };

  const handleClickHere = () => {
    onClose();
    navigate("/company-settings/settings/integration");
  };

  const handleProjectSelect = async (e) => {
    const selectedProjectKey = e.target.value;
    const selectedProject = jiraProjectList?.find(
      (project) => project.key === selectedProjectKey
    );

    const selectedProjectName = selectedProject?.projectData?.name || "";
    const selectedProjectId = selectedProject?.projectId || "";

    setTaskData((prev) => ({
      ...prev,
      jiraProjectKey: e.target.value || "",
      jiraProjectName: selectedProjectName,
      jiraProjectId: selectedProjectId,
    }));

    setFormData({});
  };

  // Handle estimation days change
  const handleEstimationDaysChange = (value: string) => {
    const estimation = updateEstimation(
      value,
      taskData.estimationHours || "",
      taskData.estimationMinutes || ""
    );
    setTaskData((prev) => ({
      ...prev,
      estimationDays: value,
      estimation: estimation,
    }));
  };

  // Handle estimation hours change
  const handleEstimationHoursChange = (value: string) => {
    const estimation = updateEstimation(
      taskData.estimationDays || "",
      value,
      taskData.estimationMinutes || ""
    );
    setTaskData((prev) => ({
      ...prev,
      estimationHours: value,
      estimation: estimation,
    }));
  };

  // Handle estimation minutes change
  const handleEstimationMinutesChange = (value: string) => {
    const estimation = updateEstimation(
      taskData.estimationDays || "",
      taskData.estimationHours || "",
      value
    );
    setTaskData((prev) => ({
      ...prev,
      estimationMinutes: value,
      estimation: estimation,
    }));
  };

  const handleSaveAttachmentAnnotation = async (annotatedImageData) => {
    try {
      const updatedAttachments = [...attachments];
      const attachment = updatedAttachments[currentAttachmentIndex];

      // Get original filename for prefix
      const originalFilename =
        originalFilenames.get(attachment.fileName) || attachment.fileName;

      // Generate unique filename for annotated attachment
      const uniqueFilename = generateUniqueFilename(
        `annotated_${originalFilename}`
      );

      // Store new mapping for display purposes
      setOriginalFilenames((prev) =>
        new Map(prev).set(uniqueFilename, `annotated_${originalFilename}`)
      );

      updatedAttachments[currentAttachmentIndex] = {
        ...attachment,
        url: annotatedImageData,
        fileName: uniqueFilename, // Use unique filename for backend
        isAnnotated: true,
        isUploading: true,
      };

      setAttachments(updatedAttachments);

      // Upload with unique filename
      const uploadUrl = await uploadScreenshot(
        annotatedImageData,
        undefined,
        `annotated_${originalFilename}`
      );

      setAttachments((prev) =>
        prev.map((att, idx) =>
          idx === currentAttachmentIndex
            ? {
                ...att,
                serverUrl: uploadUrl,
                isUploading: false,
              }
            : att
        )
      );
    } catch (error) {
      console.error("Error uploading annotated image:", error);
      setAttachments((prev) =>
        prev.map((att, idx) =>
          idx === currentAttachmentIndex
            ? {
                ...att,
                isUploading: false,
                uploadFailed: true,
              }
            : att
        )
      );
    } finally {
      setIsAnnotatingAttachment(false);
      setCurrentAttachmentIndex(null);
    }
  };

  const handleAnnotateAttachment = (index) => {
    setCurrentAttachmentIndex(index);
    setIsAnnotatingAttachment(true);
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  // Prepare combined attachments for display
  const prepareCombinedAttachments = () => {
    const combined = [];

    // Add video recording if exists
    if (taskData?.videoRecording) {
      combined.push({
        _id: "video_recording",
        name: "Video Recording",
        type: "video",
        fileType: "video/mp4",
        size: "N/A",
        date: new Date(),
        url: taskData.videoRecording,
        originalIndex: -1,
        isVideo: true,
      });
    }

    // Add screenshots if exist
    if (taskData?.fullPageScreenshot) {
      combined.push({
        _id: "fullpage_screenshot",
        name: "Full Page Screenshot",
        type: "screenshot",
        fileType: "image/png",
        size: "N/A",
        date: new Date(),
        url: taskData.fullPageScreenshot,
        originalIndex: -1,
        isScreenshot: true,
      });
    }

    // Add regular attachments
    attachments.forEach((attachment, index) => {
      const displayName =
        originalFilenames.get(attachment.fileName) || attachment.fileName;

      combined.push({
        _id: `attachment_${index}`,
        name: displayName,
        type: attachment.fileType?.startsWith("image/") ? "image" : "file",
        fileType: attachment.fileType || "application/octet-stream",
        size: "N/A",
        date: new Date(),
        url: attachment.url,
        serverUrl: attachment.serverUrl,
        isUploading: attachment.isUploading,
        uploadFailed: attachment.uploadFailed,
        isAnnotated: attachment.isAnnotated,
        originalIndex: index,
        ...attachment,
      });
    });

    return combined;
  };

  // Update combined attachments when dependencies change
  useEffect(() => {
    setCombinedAttachments(prepareCombinedAttachments());
  }, [
    attachments,
    taskData.videoRecording,
    taskData.fullPageScreenshot,
    originalFilenames,
  ]);

  useEffect(() => {
    const initializeScreenshots = async () => {
      try {
        if (screenshotData) {
          const url = await uploadScreenshot(
            screenshotData,
            undefined,
            "element_screenshot.png"
          );
          setTaskData((prev) => ({
            ...prev,
            screenShotKey: url,
          }));
        }

        if (blobDataForFullPage) {
          const url = await uploadScreenshot(
            "",
            blobDataForFullPage,
            "fullpage_screenshot.png"
          );
          setTaskData((prev) => ({
            ...prev,
            fullPageScreenshotKey: url,
          }));
        }
      } catch (error) {
        console.error("Screenshot upload failed:", error);
      }
    };

    if (screenshotData || blobDataForFullPage) {
      initializeScreenshots();
    }
  }, [screenshotData, blobDataForFullPage]);

  useEffect(() => {
    if (Array.isArray(configData) && configData?.length > 0) {
      const findJiraConfigExist = configData.find(
        (config) => config.type === "JIRA"
      );
      if (findJiraConfigExist) {
        setIsJiraConfigured(true);
        setTaskData({
          ...taskData,
          platform: "JIRA",
        });
      } else {
        setIsJiraConfigured(false);
        setTaskData({
          ...taskData,
          platform: "NONE",
        });
      }
    } else {
      setIsJiraConfigured(false);
    }
  }, [configData]);

  useEffect(() => {
    // Only apply saved settings when modal opens, not when project ID changes
    if (taskDataKeepdecryptedData && open && !taskData.project) {
      // Parse existing estimation if it exists
      let days = "";
      let hours = "";
      let minutes = "";

      if (taskDataKeepdecryptedData.estimation) {
        const estimationStr = taskDataKeepdecryptedData.estimation;
        const dayMatch = estimationStr.match(/(\d+)d/);
        const hourMatch = estimationStr.match(/(\d+)h/);
        const minuteMatch = estimationStr.match(/(\d+)m/);

        if (dayMatch) days = dayMatch[1];
        if (hourMatch) hours = hourMatch[1];
        if (minuteMatch) minutes = minuteMatch[1];
      }

      // Find the selected project to get its boardType
      const selectedProject = projectList?.find(
        (project) => project._id === (id || taskDataKeepdecryptedData.project)
      );
      const boardType = selectedProject?.boardType || "";

      setTaskData({
        ...taskData,
        platform: taskDataKeepdecryptedData.platform || "JIRA",
        assignees: taskDataKeepdecryptedData.assignees || [],
        project: id || taskDataKeepdecryptedData.project, // Use id if available
        projectKey: taskDataKeepdecryptedData.projectKey,
        severity: taskDataKeepdecryptedData.severity,
        status: taskDataKeepdecryptedData.status,
        tags: taskDataKeepdecryptedData.tags || [],
        type: taskDataKeepdecryptedData.type,
        keepSettings: taskDataKeepdecryptedData.keepSettings,
        jiraProjectKey: taskDataKeepdecryptedData.jiraProjectKey,
        jiraProjectName: taskDataKeepdecryptedData.jiraProjectName,
        jiraProjectId: taskDataKeepdecryptedData.jiraProjectId,
        startDate: taskDataKeepdecryptedData.startDate,
        dueDate: taskDataKeepdecryptedData.dueDate,
        estimation: taskDataKeepdecryptedData.estimation || "",
        estimationDays: taskDataKeepdecryptedData.estimationDays || days,
        estimationHours: taskDataKeepdecryptedData.estimationHours || hours,
        estimationMinutes:
          taskDataKeepdecryptedData.estimationMinutes || minutes,
        boardType: boardType, // Set boardType from selected project
        currentSprint: taskDataKeepdecryptedData.currentSprint || null,
        isBackLog: taskDataKeepdecryptedData.isBackLog || false,
      });
    }
  }, [open, id, projectList]); // Add id as dependency
  useEffect(() => {
    if (open && screenshotData) {
      const initializeScreenshots = async () => {
        setTaskData((prev) => ({
          ...prev,
          screenshot: screenshotData,
          selector: selector,
        }));
      };
      initializeScreenshots();
    }
    if (fullPageScreenshotData) {
      setTaskData((prev) => ({
        ...prev,
        fullPageScreenshot: fullPageScreenshotData,
      }));
    }
  }, [open, screenshotData, selector, fullPageScreenshotData]);

  useEffect(() => {
    const fetchStatusLabels = async () => {
      if (taskData?.jiraProjectKey) {
        try {
          const response = await getProjectStatusLabels({
            keys: [taskData?.jiraProjectKey],
          });
          if (response?.data?.statuses) {
            setStatusList(response.data.statuses);
          }
        } catch (err) {
          console.error("Error fetching status labels:", err);
        }
      }
    };

    fetchStatusLabels();
  }, [taskData.jiraProjectKey]);

  useEffect(() => {
    if (videoRecording) {
      setTaskData((prev) => ({
        ...prev,
        screenshot: null,
        fullPageScreenshot: null,
        screenShotKey: "",
        fullPageScreenshotKey: "",
        recordingLink: "",
        videoRecording: videoRecording,
      }));
    }
  }, [videoRecording, status]);

  useEffect(() => {
    if (jiraProjectList?.length > 0) {
      setTaskData((prev) => ({
        ...prev,
        jiraProjectKey: null,
      }));
    }
  }, [jiraProjectList]);

  useEffect(() => {
    if (jiraProjectList?.length > 0 && defaultFields) {
      const initialFormData = {};
      Object.entries(defaultFields).forEach(([fieldLabel, fieldData]) => {
        const fieldKey = Object.keys(fieldData)[0];
        initialFormData[fieldKey] = fieldData[fieldKey];
      });
      setFormData(initialFormData);
    }
  }, [jiraProjectList, defaultFields]);

  useEffect(() => {
    ``;
    if (taskData.project) {
      if (!boardState?.selectedColumnStatus) {
        setTaskData((prev) => ({
          ...prev,
          status: "",
        }));
        return;
      }

      setStatusError(""); // Clear status error when project changes
    }
  }, [taskData.project, boardState?.selectedColumnStatus]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        onPaste={handleSystemPaste}
        sx={{
          "& .MuiDrawer-paper": {
            width: "90vw",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            px: 5,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="h6"
              sx={{ color: "#333", fontWeight: 600, mr: 2 }}
            >
              {t("Create Task")}
            </Typography>
            <TabContext value={activeTab}>
              <Box sx={{ borderBottom: 0, borderColor: "divider" }}>
                <TabList
                  onChange={handleTabChange}
                  aria-label="lab API tabs example"
                  TabIndicatorProps={{ style: { display: "none" } }}
                  sx={{
                    height: "100%",
                    minHeight: "unset",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      minHeight: "27px",
                      height: "27px",
                      marginRight: 0,
                      border: "1px solid #d6d6d6",
                    },

                    "& .Mui-selected": {
                      backgroundColor: (theme) =>
                        hexToRgba(theme.palette.primary.main, 0.05),
                      fontWeight: "bold",
                      border: (theme) =>
                        `1.5px solid ${theme.palette.primary.main}`,
                      "&:first-child": {
                        borderRadius: "4px 0px 0px 4px",
                      },
                      "&:last-child": {
                        borderRadius: "0px 4px 4px 0px",
                      },
                    },
                  }}
                >
                  <Tab label={t("General Info")} value="1" />
                  <Tab
                    label={t("Required Info")}
                    value="2"
                    disabled={
                      !defaultFields || Object.keys(defaultFields)?.length === 0
                    }
                  />
                  <Tab label={t("Logs Info")} value="3" />
                </TabList>
              </Box>
            </TabContext>
          </Box>
          <Box display="flex" alignItems="center">
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mr: 1 }}
              size="small"
            >
              {t("Cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              onClick={handleButtonClick}
              loading={isSubmitting || recordBugLoading || isLoading}
              disabled={
                (taskData?.platform === "JIRA" && !taskData?.project) ||
                !taskData?.summary ||
                hasPendingUploads()
              }
            >
              {hasPendingUploads() ? t("Uploading...") : t("Create Task")}
            </LoadingButton>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <TabContext value={activeTab}>
            <TabPanel value="1" sx={{ p: 0 }}>
              {/* Task Summary - Full Width */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  placeholder={t("Add summary")}
                  name="summary"
                  value={taskData?.summary}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  label={t("Summary")}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  {taskData?.summary?.length || 0} / 250
                </Typography>
              </Box>

              {/* Attach Option */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  gap: 3,
                }}
              >
                {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      id="file-upload"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                    />
                    <label htmlFor="file-upload">
                      <IconButton component="span" size="small">
                        <AttachFileIcon />
                      </IconButton>
                    </label>
                    <Typography variant="body2" color="text.secondary">
                      {t("Attach")}
                    </Typography>
                  </Box>
                </Box> */}
              </Box>

              {/* Divider */}
              <Divider sx={{ mb: 3 }} />

              {/* Two Equal Width Grids */}
              <Grid2 container spacing={3} sx={{ mb: 3 }}>
                {/* Left Grid */}
                <Grid2 size={6}>
                  {/* Assigned To */}
                  <FormControl fullWidth sx={{ mb: 2.6 }}>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Assignee")}
                    </InputLabel>
                    <Autocomplete
                      multiple
                      limitTags={4}
                      size="small"
                      options={availableUsers || []}
                      getOptionLabel={(option) => option.fullName}
                      value={
                        availableUsers?.filter((user) =>
                          taskData.assignees.includes(user._id)
                        ) || []
                      }
                      onChange={handleAssigneeChange}
                      disabled={!taskData.project}
                      renderTags={(tagValues, getTagProps) =>
                        tagValues.map((option, index) => (
                          <Chip
                            key={option._id}
                            label={option.fullName}
                            size="small"
                            avatar={
                              <Avatar
                                sx={{
                                  bgcolor: theme.palette.primary.main,
                                  color: "#ffffff !important",
                                  width: 20,
                                  height: 20,
                                  fontSize: "10px",
                                }}
                              >
                                {option?.fullName?.charAt(0)}
                              </Avatar>
                            }
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t("Select Assignee(s)")}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  </FormControl>

                  {/* Status */}
                  <FormControl fullWidth sx={{ mb: 4.5 }} error={!!statusError}>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: statusError
                          ? theme.palette.error.main
                          : theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Status")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main }}
                      >
                        *
                      </Box>
                    </InputLabel>
                    <Select
                      size="small"
                      value={taskData?.status || ""}
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider,
                        },
                      }}
                      onChange={handleSelectChange}
                      name="status"
                      disabled={!taskData.project}
                      error={!!statusError}
                    >
                      <MenuItem disabled value="">
                        {t("Select Status")}
                      </MenuItem>
                      {isLoadingStatusLabels ? (
                        <MenuItem disabled>{t("Loading Statuses")}</MenuItem>
                      ) : (
                        availableStatus?.map((option) => (
                          <MenuItem key={option._id} value={option._id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor:
                                    option.color || option.bgColor || "#f44336",
                                  borderRadius: "2px",
                                }}
                              />
                              {option.status}
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {statusError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {statusError}
                      </Typography>
                    )}
                  </FormControl>
                  {taskData.type !== "EPIC" &&
                    taskData?.boardType === "SCRUM" && (
                      <>
                        {/* Start Date */}
                        <StyledTextField
                          label={t("Start Date")}
                          type="date"
                          name="startDate"
                          value={taskData.startDate || ""}
                          onChange={(e) =>
                            setTaskData((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                            }))
                          }
                          size="small"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          fullWidth
                          sx={{ mb: 2 }}
                        />

                        {/* Due Date */}
                        <StyledTextField
                          label={t("Due Date")}
                          type="date"
                          name="dueDate"
                          value={taskData.dueDate || ""}
                          onChange={(e) =>
                            setTaskData((prev) => ({
                              ...prev,
                              dueDate: e.target.value,
                            }))
                          }
                          size="small"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          fullWidth
                          sx={{ mb: 2 }}
                        />
                      </>
                    )}

                  {/* Tags */}
                  <FormControl fullWidth>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Tags")}
                    </InputLabel>
                    <Autocomplete
                      multiple
                      freeSolo
                      limitTags={2}
                      options={[
                        ...availableTags,
                        tagInput &&
                        !availableTags?.some((tag) => tag.tagName === tagInput)
                          ? { tagName: tagInput }
                          : null,
                      ]?.filter(Boolean)}
                      getOptionLabel={(option) => option.tagName}
                      disabled={!taskData?.project}
                      disableCloseOnSelect
                      value={availableTags.filter((tag) =>
                        taskData.tags.includes(tag.tagName)
                      )}
                      size="small"
                      onChange={async (_, newValues) => {
                        if (
                          tagInput &&
                          !newValues.some((tag) => tag.tagName === tagInput)
                        ) {
                          return;
                        }

                        const newTag = newValues.find(
                          (tag) =>
                            !availableTags?.some(
                              (t) => t.tagName === tag.tagName
                            )
                        );

                        if (newTag) {
                          const payload = {
                            project: taskData.project,
                            tagName: newTag.tagName,
                          };
                          setIsCreatingTag(true);
                          try {
                            const res = await createTag(payload).unwrap();
                            if (res) {
                              await refetchTags();
                            }
                          } catch (error) {
                            console.error("Error creating tag:", error);
                            toast.error(t("Failed to create tag"));
                          } finally {
                            setIsCreatingTag(false);
                          }
                        }

                        setTaskData((prev) => ({
                          ...prev,
                          tags: newValues.map((tag) => tag.tagName),
                        }));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                        }
                      }}
                      onInputChange={(_event, newInputValue) => {
                        setTagInput(newInputValue);
                      }}
                      renderTags={(tagValues, getTagProps) =>
                        tagValues?.map((option, index) => (
                          <StyledChip
                            {...getTagProps({ index })}
                            key={option._id || option.tagName}
                            label={option.tagName}
                            size="small"
                            icon={
                              <LocalOfferOutlinedIcon
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontSize: "14px",
                                }}
                              />
                            }
                          />
                        ))
                      }
                      renderOption={(props, option, { selected }) => {
                        const isCustomTag =
                          option.tagName === tagInput && tagInput !== "";
                        return (
                          <li {...props}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              {!isCustomTag ? (
                                <LocalOfferOutlinedIcon
                                  sx={{ marginRight: 1, fontSize: "15px" }}
                                />
                              ) : null}
                              <Typography
                                sx={{
                                  flexGrow: 1,
                                  fontSize: "13px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                {isCustomTag ? (
                                  <>
                                    <AddCircleOutlineOutlinedIcon
                                      sx={{
                                        fontSize: "18px",
                                        mr: 1,
                                        color: "#4caf50",
                                      }}
                                    />
                                    {tagInput}{" "}
                                  </>
                                ) : (
                                  option.tagName
                                )}
                              </Typography>
                              {selected && (
                                <CheckIcon
                                  sx={{
                                    color: theme.palette.primary.main,
                                    ml: 1,
                                  }}
                                />
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder={t("Add Tags")}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isCreatingTag ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            marginBottom: "0px",
                            "& .MuiInputBase-root": {
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              alignItems: "center",
                              p: 1,
                            },
                            "& .MuiInputBase-input": {
                              position: "relative",
                              width: "auto",
                              minWidth: "60px",
                              flexGrow: 1,
                            },
                          }}
                        />
                      )}
                      sx={{
                        mb: 2,
                        "& .MuiAutocomplete-tag": {
                          margin: "2px",
                        },
                        "& .MuiInput-root": {
                          display: "flex",
                          flexWrap: "wrap",
                          border: `1px solid ${theme.palette.divider}`,
                          gap: "4px",
                          alignItems: "flex-start",
                          padding: "8px",
                          maxHeight: "120px",
                          overflow: "auto",
                        },
                      }}
                    />
                  </FormControl>

                  {/* {!taskData?.fullPageScreenshot &&
                    !taskData?.videoRecording &&
                    recordBugLoading && (
                      <Box
                        mt={2}
                        mb={1}
                        display="flex"
                        gap={1}
                        fontSize="0.875rem"
                        fontStyle="italic"
                        color="#c8c8c8"
                        alignItems="center"
                      >
                        <Box>{t("Processing Video...")}</Box>
                        <CircularProgress size={16} />
                      </Box>
                    )} */}

                  {/* {taskData?.videoRecording && (
                    <>
                      <Box
                        mt={1}
                        sx={{
                          backgroundColor: theme.palette.grey[100],
                          padding: theme.spacing(1),
                          paddingBottom: theme.spacing(1),
                          minHeight: "200px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2">
                            {t("Video recorded")}
                          </Typography>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={handleRemoveVideoRecording}
                              title={t("Delete video recording")}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="100%"
                          border="1px solid #d6d6d6"
                          mt={1}
                        >
                          <video
                            src={taskData?.videoRecording}
                            controls
                            style={{ width: "100%" }}
                          />
                        </Box>
                      </Box>
                    </>
                  )} */}

                  {!taskData?.fullPageScreenshot &&
                    !taskData?.videoRecording && (
                      <>
                        <StyledTextField
                          placeholder={t("Recording Link")}
                          name="recordingLink"
                          value={recordedURL || taskData?.recordingLink}
                          onChange={handleInputChange}
                          variant="outlined"
                          fullWidth
                          sx={{ mb: 1, mt: !recordBugLoading && 2 }}
                          disabled={
                            recordBugLoading || recordedURL ? true : false
                          }
                          label={t("Recording Link")}
                          size="small"
                        />
                      </>
                    )}
                  {/* Sprint Selection - Only show for SCRUM board type */}
                  {(taskData.boardType === "SCRUM" ||
                    boardState?.dueDateDateForCalendarView) &&
                    taskData?.type !== "EPIC" && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel
                          shrink
                          sx={{
                            position: "relative",
                            transform: "none",
                            fontSize: "13px",
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {t("Sprint")}
                        </InputLabel>
                        <Select
                          size="small"
                          value={taskData.currentSprint || ""}
                          onChange={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const selectedValue = e.target.value as string;
                            setTaskData((prev) => {
                              return {
                                ...prev,
                                currentSprint: selectedValue || null,
                              };
                            });
                          }}
                          name="currentSprint"
                          disabled={!taskData.project || isSprintLoading}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.divider,
                            },
                          }}
                        >
                          <MenuItem value="">
                            {isSprintLoading
                              ? t("Loading sprints...")
                              : t("No Sprint")}
                          </MenuItem>

                          {/* Active Sprints */}
                          {activeSprints.length > 0 && [
                            <MenuItem
                              key="active-header"
                              disabled
                              sx={{
                                opacity: 1,
                                fontWeight: "bold",
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                "&.Mui-disabled": {
                                  opacity: 1,
                                },
                                pointerEvents: "none", // Prevent any interaction
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.primary",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("Active Sprints")}
                              </Typography>
                            </MenuItem>,
                            ...activeSprints.map((sprint, index) => (
                              <MenuItem
                                key={`active-sprint-${sprint._id}-${index}`}
                                value={sprint._id}
                                onClick={(e) => {}}
                                sx={{
                                  pl: 3,
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    width: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      backgroundColor: "#4caf50",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <Typography sx={{ fontSize: "0.875rem" }}>
                                    {sprint.name}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            )),
                          ]}

                          {/* Draft Sprints */}
                          {draftSprints.length > 0 && [
                            <MenuItem
                              key="draft-header"
                              disabled
                              sx={{
                                opacity: 1,
                                fontWeight: "bold",
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                "&.Mui-disabled": {
                                  opacity: 1,
                                },
                                pointerEvents: "none", // Prevent any interaction
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.primary",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("Draft Sprints")}
                              </Typography>
                            </MenuItem>,
                            ...draftSprints.map((sprint, index) => (
                              <MenuItem
                                key={`draft-sprint-${sprint._id}-${index}`}
                                value={sprint._id}
                                onClick={(e) => {}}
                                sx={{
                                  pl: 3,
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    width: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      backgroundColor: "#ff9800",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <Typography sx={{ fontSize: "0.875rem" }}>
                                    {sprint.name}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            )),
                          ]}
                        </Select>
                      </FormControl>
                    )}

                  {/* {taskData?.fullPageScreenshot &&
                    !taskData?.videoRecording && (
                      <ScreenshotContainer>
                        {taskData?.fullPageScreenshot ? (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography variant="body2">
                                {t("Screenshot captured")}
                              </Typography>
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setIsAnnotationEditorOpen(true)
                                  }
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleRemoveScreenshot}
                                  title={t("Delete screenshot")}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                            <img
                              src={taskData?.fullPageScreenshot}
                              alt={t("Screenshot")}
                              style={{
                                width: "100%",
                                maxHeight: "200px",
                                objectFit: "contain",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedImage(taskData?.fullPageScreenshot);
                                setIsImagePreviewOpen(true);
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {t("No screenshot captured")}
                          </Typography>
                        )}
                      </ScreenshotContainer>
                    )} */}
                </Grid2>

                {/* Right Grid */}
                <Grid2 size={6}>
                  {/* Project */}
                  <FormControl fullWidth error={!!projectError} sx={{ mb: 0 }}>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: projectError
                          ? theme.palette.error.main
                          : theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Project")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main }}
                      >
                        *
                      </Box>
                    </InputLabel>
                    <StyledSelect
                      value={taskData?.project || ""}
                      onChange={handleSelectChange}
                      name="project"
                      fullWidth
                      size="small"
                      error={!!projectError}
                    >
                      {projectList?.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    {projectError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5 }}
                      >
                        {projectError}
                      </Typography>
                    )}
                  </FormControl>

                  {/* Jira Project */}
                  {jiraProjectList?.length > 0 && (
                    <Box
                      display={"flex"}
                      gap={1}
                      alignItems={"center"}
                      alignContent={"center"}
                      sx={{ mb: 2 }}
                    >
                      <FormControl fullWidth>
                        <InputLabel
                          shrink
                          sx={{
                            position: "relative",
                            transform: "none",
                            fontSize: "13px",
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {t("Jira Project")}
                        </InputLabel>
                        <StyledSelect
                          value={taskData.jiraProjectKey || ""}
                          onChange={(e) => {
                            handleProjectSelect(e);
                          }}
                          name="jiraProjectKey"
                          disabled={
                            !isJiraConfigured ||
                            isProjectLoading ||
                            !taskData?.project
                          }
                          size="small"
                        >
                          <MenuItem disabled value="">
                            {isProjectLoading
                              ? t("Checking JIRA configuration...")
                              : isJiraConfigured
                              ? t("Select Jira Project")
                              : t("Select Jira Project")}
                          </MenuItem>
                          {isProjectLoading ? (
                            <MenuItem disabled>
                              {t("Loading projects")}
                            </MenuItem>
                          ) : (
                            Array.isArray(jiraProjectList) &&
                            jiraProjectList?.map((project) => (
                              <MenuItem key={project.key} value={project.key}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  width={"100%"}
                                >
                                  <Avatar
                                    src={
                                      project?.projectData?.avatarUrls["48x48"]
                                    }
                                    sx={{ width: 24, height: 24, mr: 1 }}
                                  />
                                  <Typography>
                                    {project?.projectData?.name} (
                                    {project?.projectData?.key})
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))
                          )}
                        </StyledSelect>
                      </FormControl>
                    </Box>
                  )}

                  {/* JIRA Configuration Warning */}
                  {!isJiraConfigured && !isProjectLoading && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mb: 2, mt: -1 }}
                    >
                      {t(
                        "JIRA integration is not configured or does not exist any project. Click"
                      )}{" "}
                      <span
                        onClick={handleClickHere}
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          color: "secondary.main",
                        }}
                      >
                        {t("here")}
                      </span>{" "}
                      {t("to integrate.")}
                    </Typography>
                  )}

                  {/* Priority */}
                  <FormControl fullWidth error={!!severityError} sx={{ mb: 2 }}>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: severityError
                          ? theme.palette.error.main
                          : theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Severity")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main }}
                      >
                        *
                      </Box>
                    </InputLabel>
                    <Select
                      id="demo-simple-select"
                      value={taskData?.severity}
                      onChange={handleSelectChange}
                      name="severity"
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider,
                        },
                      }}
                      error={!!severityError}
                    >
                      <MenuItem disabled value="">
                        {t("Select Priority")}
                      </MenuItem>
                      {severityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ListItemIcon
                              sx={{
                                minWidth: "auto",
                                width: 20,
                                height: 20,
                              }}
                            >
                              {option.icon}
                            </ListItemIcon>
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {severityError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5 }}
                      >
                        {severityError}
                      </Typography>
                    )}
                  </FormControl>

                  {/* Estimation - Days, Hours and Minutes */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t("Estimation")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        label={t("Days")}
                        type="number"
                        value={taskData.estimationDays || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (
                            value === "" ||
                            (parseInt(value) >= 0 && !isNaN(parseInt(value)))
                          ) {
                            handleEstimationDaysChange(value);
                          }
                        }}
                        inputProps={{
                          min: 0,
                          step: 1,
                          onKeyDown: (e) => {
                            if (["-", "e", "E", "+", "."].includes(e.key)) {
                              e.preventDefault();
                            }
                          },
                        }}
                        sx={{ minWidth: 80 }}
                      />
                      <TextField
                        size="small"
                        label={t("Hours")}
                        type="number"
                        value={taskData.estimationHours || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (
                            value === "" ||
                            (parseInt(value) >= 0 &&
                              parseInt(value) <= 23 &&
                              !isNaN(parseInt(value)))
                          ) {
                            handleEstimationHoursChange(value);
                          }
                        }}
                        inputProps={{
                          min: 0,
                          max: 23,
                          step: 1,
                          onKeyDown: (e) => {
                            if (["-", "e", "E", "+", "."].includes(e.key)) {
                              e.preventDefault();
                            }
                          },
                        }}
                        sx={{ minWidth: 80 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel
                          sx={{
                            transform: "translate(14px, 9px) scale(1)",
                            "&.Mui-focused, &.MuiFormLabel-filled": {
                              transform: "translate(14px, -9px) scale(0.75)",
                            },
                          }}
                        >
                          {t("Minutes")}
                        </InputLabel>
                        <Select
                          value={taskData.estimationMinutes || ""}
                          onChange={(e) =>
                            handleEstimationMinutesChange(
                              e.target.value as string
                            )
                          }
                          label={t("Minutes")}
                        >
                          {minuteOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {taskData.estimation && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {t("Estimation")}: {taskData.estimation}
                      </Typography>
                    )}
                  </Box>

                  {/* Issue Type */}
                  <FormControl
                    fullWidth
                    error={!!issueTypeError}
                    sx={{ mb: 2 }}
                  >
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: issueTypeError
                          ? theme.palette.error.main
                          : theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Issue Type")}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.error.main }}
                      >
                        *
                      </Box>
                    </InputLabel>
                    <Select
                      id="demo-simple-select"
                      value={taskData?.type}
                      onChange={handleSelectChange}
                      name="type"
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider,
                        },
                      }}
                      error={!!issueTypeError}
                    >
                      <MenuItem disabled value="">
                        {t("Select Issue Type")}
                      </MenuItem>
                      {issueTypeItems.map((option) => (
                        <MenuItem
                          key={option?.value || option?.code}
                          value={option.value || option?.code}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            width={"100%"}
                          >
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {issueTypeError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5 }}
                      >
                        {issueTypeError}
                      </Typography>
                    )}
                  </FormControl>
                  {/* Epic Selection – show only if issue type is NOT 'EPIC' */}
                  {taskData.type !== "EPIC" &&
                    taskData?.boardType === "SCRUM" && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel
                          shrink
                          sx={{
                            position: "relative",
                            transform: "none",
                            fontSize: "13px",
                            color: theme.palette.text.primary,
                            mb: 0.5,
                          }}
                        >
                          {t("Epic")}
                        </InputLabel>
                        <Select
                          name="epic"
                          value={selectedEpic || ""}
                          onChange={(e) => setSelectedEpic(e.target.value)}
                          label={t("Epic")}
                          size="small"
                          displayEmpty
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.divider,
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>{t("None")}</em>
                          </MenuItem>
                          {epics.map((epic) => (
                            <MenuItem key={epic.id} value={epic.id}>
                              {epic.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  {/* Required Skill Set - Add after Epic field */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Required Skill Set")}
                    </InputLabel>
                    <Autocomplete
                      options={skillSetOptions}
                      value={taskData.requiredSkillSet || null}
                      onChange={(event, newValue) => {
                        setTaskData((prev) => ({
                          ...prev,
                          requiredSkillSet: newValue || "",
                        }));
                      }}
                      disabled={!taskData.project}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t("Select required skill")}
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              paddingTop: "4px",
                              paddingBottom: "4px",
                            },
                          }}
                        />
                      )}
                    />
                  </FormControl>

                  {/* Relationship */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel
                      shrink
                      sx={{
                        position: "relative",
                        transform: "none",
                        fontSize: "13px",
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {t("Relationship")}
                    </InputLabel>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setDependenciesAnchorEl(e.currentTarget)}
                      disabled={!taskData.project}
                      sx={{
                        justifyContent: "flex-start",
                        height: "40px",
                        textTransform: "none",
                        color: theme.palette.text.secondary,
                        borderColor: theme.palette.divider,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      {(() => {
                        const totalDeps = dependencies.length;
                        return totalDeps > 0
                          ? `${totalDeps} ${t("Dependencies")}`
                          : t("Add Dependencies");
                      })()}
                    </Button>
                  </FormControl>

                  {/* Updated Set Recurring */}
                  <FormGroup sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isRecurring}
                          onChange={handleRecurringToggle}
                          name="setRecurring"
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {t("Set Recurring")}
                          </Typography>
                          {isRecurring && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleRecurringConfigClick}
                              startIcon={<SettingsIcon />}
                              sx={{
                                fontSize: "0.75rem",
                                minWidth: "auto",
                                px: 1,
                                py: 0.5,
                              }}
                            >
                              {getRecurringDisplayText() || t("Configure")}
                            </Button>
                          )}
                        </Box>
                      }
                    />
                  </FormGroup>

                  {/* Keep Settings */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={taskData?.keepSettings}
                        onChange={(e) =>
                          setTaskData((prev) => ({
                            ...prev,
                            keepSettings: e.target.checked,
                          }))
                        }
                        name="keepSettings"
                        disabled={!taskData.project}
                      />
                    }
                    label={t("Keep these settings")}
                  />
                </Grid2>
              </Grid2>

              {/* Second Divider */}
              <Divider sx={{ mb: 3 }} />

              {/* Description - Full Width */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t("Description")}
                </Typography>
                <Box
                  sx={{
                    height: "250px",
                    maxHeight: "250px",
                    overflow: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    display: "flex",
                    flexDirection: "column",
                    "& .editor-container": {
                      height: "100% !important",
                      maxHeight: "100% !important",
                    },
                    "& .quill": {
                      height: "100% !important",
                      display: "flex !important",
                      flexDirection: "column !important",
                    },
                    "& .ql-container": {
                      flex: "1 !important",
                      overflow: "auto !important",
                    },
                    "& .ql-editor": {
                      height: "auto !important",
                      maxHeight: "none !important",
                      overflow: "auto !important",
                    },
                  }}
                >
                  <RichTextEditor
                    onChange={handleRichTextEditor}
                    value={taskData?.description}
                    placeholder={t("Enter your description here...")}
                    readOnly={false}
                    style={{ header: false }}
                  />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {stripHtmlTags(taskData?.description)?.length || 0} / 2000
                </Typography>
              </Box>
              {/* Drag & Drop Zone */}
              {combinedAttachments.length === 0 && (
                <Box sx={{ mb: 3 }}>
                  <DropZone
                    className={isDragOver ? "drag-over" : ""}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                      document.getElementById("file-upload")?.click()
                    }
                  >
                    <CloudUploadIcon
                      sx={{
                        fontSize: 40,
                        color: "text.secondary",
                        mb: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t("Drop files to attach or")}{" "}
                      <strong>{t("Browse")}</strong>
                    </Typography>
                  </DropZone>

                  <input
                    type="file"
                    multiple
                    accept="*/*"
                    id="file-upload"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />
                </Box>
              )}

              {/* Attachments & Screenshots Section */}
              {combinedAttachments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  {/* Header with View Toggle */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      {t("Attachments & Screenshots")} (
                      {combinedAttachments.length})
                    </Typography>
                    <ToggleButtonGroup
                      value={attachmentView}
                      exclusive
                      onChange={(_, newView) => {
                        if (newView !== null) {
                          setAttachmentView(newView);
                        }
                      }}
                      size="small"
                    >
                      <ToggleButton value="grid" aria-label="grid view">
                        <ViewModuleIcon />
                      </ToggleButton>
                      <ToggleButton value="list" aria-label="list view">
                        <ViewListIcon />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* List View */}
                  {attachmentView === "list" && (
                    <AttachmentListView
                      attachments={combinedAttachments}
                      isLoading={isUploading || combiningAttachments}
                      onView={handleViewAttachment}
                      onEdit={(attachment) => {
                        if (attachment.type === "screenshot") {
                          setIsAnnotationEditorOpen(true);
                        } else if (
                          attachment.fileType?.startsWith("image/") &&
                          attachment.originalIndex >= 0
                        ) {
                          handleAnnotateAttachment(attachment.originalIndex);
                        }
                      }}
                      onDelete={(attachment) => {
                        if (attachment.originalIndex >= 0) {
                          handleRemoveAttachment(attachment.originalIndex);
                        } else if (attachment.type === "screenshot") {
                          handleRemoveScreenshot();
                        } else if (attachment.type === "video") {
                          handleRemoveVideoRecording();
                        }
                      }}
                      anchorEl={anchorEl}
                      selectedRowForMenu={selectedRowForMenu}
                      onMenuOpen={handleMenuOpen}
                      onMenuClose={handleMenuClose}
                      onFileUpload={(files) => processFiles(files)}
                    />
                  )}

                  {/* Grid View */}
                  {attachmentView === "grid" && (
                    <AttachmentGridView
                      attachments={combinedAttachments}
                      isLoading={isUploading || combiningAttachments}
                      onView={handleViewAttachment}
                      onEdit={(attachment) => {
                        if (attachment.type === "screenshot") {
                          setIsAnnotationEditorOpen(true);
                        } else if (
                          attachment.fileType?.startsWith("image/") &&
                          attachment.originalIndex >= 0
                        ) {
                          handleAnnotateAttachment(attachment.originalIndex);
                        }
                      }}
                      onDelete={(attachment) => {
                        if (attachment.originalIndex >= 0) {
                          handleRemoveAttachment(attachment.originalIndex);
                        } else if (attachment.type === "screenshot") {
                          handleRemoveScreenshot();
                        } else if (attachment.type === "video") {
                          handleRemoveVideoRecording();
                        }
                      }}
                      anchorEl={anchorEl}
                      selectedRowForMenu={selectedRowForMenu}
                      onMenuOpen={handleMenuOpen}
                      onMenuClose={handleMenuClose}
                      onFileUpload={(files) => processFiles(files)}
                    />
                  )}
                </Box>
              )}
            </TabPanel>

            <TabPanel value="2" sx={{ p: 0 }}>
              <Grid2 container spacing={3}>
                <Grid2 size={6}>
                  {jiraProjectList?.length > 0 &&
                    taskData.project &&
                    defaultFields &&
                    Object.entries(defaultFields)?.map(
                      ([fieldLabel, fieldData]: any) => {
                        const fieldKeys = Object.keys(fieldData).filter(
                          (key) => key !== "method" && key !== "type"
                        );
                        const fieldKey = fieldKeys[0];
                        const defaultValue = fieldData[fieldKey];
                        const type = fieldData.type;

                        if (fieldData?.allowedValues) {
                          return (
                            <StyledSelect
                              key={fieldLabel}
                              value={formData[fieldKey] || ""}
                              onChange={(e) => {
                                // @ts-ignore
                                handleChange(fieldKey, e.target.value, type);
                              }}
                              name={fieldLabel}
                              displayEmpty
                              fullWidth
                            >
                              <MenuItem selected disabled value="">
                                {fieldLabel}
                              </MenuItem>
                              {fieldData?.allowedValues?.map((option) => (
                                <MenuItem key={option?.id} value={option?.id}>
                                  {option?.name}
                                </MenuItem>
                              ))}
                            </StyledSelect>
                          );
                        }

                        return (
                          <Box key={fieldLabel} sx={{ my: 2 }}>
                            <TextField
                              fullWidth
                              type={type}
                              label={fieldLabel}
                              value={formData[fieldKey] || defaultValue}
                              onChange={(e) => {
                                handleChange(fieldKey, e.target.value, type);
                              }}
                              inputProps={
                                type === "number"
                                  ? {
                                      min: 0,
                                      onKeyDown: (e) => {
                                        if (["-", "e", "E"].includes(e.key)) {
                                          e.preventDefault();
                                        }
                                      },
                                    }
                                  : undefined
                              }
                            />
                          </Box>
                        );
                      }
                    )}
                </Grid2>
                <Grid2 size={6}></Grid2>
              </Grid2>
            </TabPanel>
            <TabPanel value="3">
              <LogsTab
                consoleLogs={consoleLogs}
                networkLogs={networkLogs}
                systemInfo={systemInfo}
              />
            </TabPanel>
          </TabContext>
        </Box>
      </Drawer>

      {/* Modals */}
      <AnnotationEditor
        open={isAnnotationEditorOpen}
        onClose={() => setIsAnnotationEditorOpen(false)}
        imageUrl={taskData?.fullPageScreenshot || ""}
        onSave={handleSaveAnnotation}
        onInsertText={(text) => setTaskData((prev) => ({ ...prev, description: (prev.description || "") + "\n" + text }))}
      />

      <AnnotationEditor
        open={isAnnotatingAttachment}
        onClose={() => {
          setIsAnnotatingAttachment(false);
          setCurrentAttachmentIndex(null);
        }}
        imageUrl={
          currentAttachmentIndex !== null && attachments[currentAttachmentIndex]
            ? attachments[currentAttachmentIndex].url || ""
            : ""
        }
        onSave={handleSaveAttachmentAnnotation}
        onInsertText={(text) => setTaskData((prev) => ({ ...prev, description: (prev.description || "") + "\n" + text }))}
      />

      <AttachmentViewModal
        open={isAttachmentViewOpen}
        onClose={() => setIsAttachmentViewOpen(false)}
        attachment={selectedAttachment}
      />

      <Dialog
        open={isImagePreviewOpen}
        onClose={() => setIsImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <IconButton
          onClick={() => setIsImagePreviewOpen(false)}
          sx={{
            color: (theme) => theme.palette.grey[500],
            position: "absolute",
            right: 16,
            top: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{ padding: 2, display: "flex", justifyContent: "center", mt: 5 }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Preview"
              style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </Box>
      </Dialog>

      {/* <TaskDependencies
        open={Boolean(dependenciesAnchorEl)}
        anchorEl={dependenciesAnchorEl}
        onClose={() => setDependenciesAnchorEl(null)}
        dependencies={dependencies}
        onUpdateDependencies={setDependencies}
        projectId={taskData.project}
      /> */}
      <Relationship
        open={Boolean(dependenciesAnchorEl)}
        onClose={() => setDependenciesAnchorEl(null)}
        dependencies={dependencies}
        onUpdateDependencies={setDependencies}
        projectId={taskData.project}
        projectList={projectList || []}
        isTaskUpdate={false}
      />

      {/* Updated RecurringTaskPopover */}
      <RecurringTaskPopover
        open={isRecurringModalOpen}
        anchorEl={recurringAnchorEl}
        onClose={handleRecurringModalClose}
        onSave={handleRecurringConfigSave}
        initialConfig={recurringConfig}
        availableStatus={availableStatus}
      />

      {/* Video Preview Panel Modal */}
      <Dialog
        open={isVideoPreviewOpen}
        onClose={() => setIsVideoPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <VideoPreviewPanel
          videoUrl={previewVideoUrl || ""}
          networkLogs={networkLogs || []}
          consoleLogs={consoleLogs || []}
          onClose={() => setIsVideoPreviewOpen(false)}
          isOpen={isVideoPreviewOpen}
        />
      </Dialog>
    </>
  );
};

export default TaskCreateDrawer;
